/**
 * ChatComposer — the message box, in the shape current AI apps use: a raised rounded card with
 * the text on top and a row underneath holding an optional action on the left and a round send
 * button on the right. Enter sends, Shift+Enter adds a line.
 *
 * Controlled: the caller owns the value (react-hook-form `Controller` in CareStart, page state in
 * the Medical Library), so this holds no form state of its own.
 *
 * `autoFocus` keeps the cursor in the box: on mount and whenever sending becomes possible again
 * (e.g. after a reply). Skipped on touch screens, where focusing pops the keyboard over the
 * question the user needs to read.
 */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, InputBase, Paper } from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';

function ChatComposer({
  value,
  onChange,
  onSubmit,
  onBlur,
  name,
  inputRef: externalInputRef,
  placeholder = 'Message',
  ariaLabel,
  disabled = false,
  sendDisabled = false,
  autoFocus = false,
  startAction = null,
}) {
  const inputRef = useRef(null);
  const canSend = Boolean(value?.trim()) && !disabled && !sendDisabled;

  useEffect(() => {
    if (!autoFocus || disabled || sendDisabled || !inputRef.current) return;
    if (window.matchMedia?.('(pointer: coarse)').matches) return;
    inputRef.current.focus({ preventScroll: true });
  }, [autoFocus, disabled, sendDisabled]);

  const submit = () => {
    if (canSend) onSubmit(value);
  };

  return (
    <Paper
      component="form"
      // A thin border at rest and the theme's lightest shadow while typing, like Google's
      // search box, rather than a heavy shadow under the box all the time.
      variant="outlined"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      sx={{ borderRadius: 6, px: 2.5, pt: 2, pb: 1.25, "&:focus-within": { boxShadow: 1 } }}
    >
      <InputBase
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        inputRef={(el) => {
          inputRef.current = el;
          if (typeof externalInputRef === 'function') externalInputRef(el);
          else if (externalInputRef) externalInputRef.current = el;
        }}
        fullWidth
        multiline
        maxRows={8}
        disabled={disabled}
        placeholder={placeholder}
        inputProps={{ 'aria-label': ariaLabel || placeholder }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        {startAction}
        <IconButton
          type="submit"
          size="small"
          disabled={!canSend}
          aria-label="Send"
          sx={{
            ml: 'auto',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
          }}
        >
          <ArrowUpwardRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}

ChatComposer.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  name: PropTypes.string,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  sendDisabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  startAction: PropTypes.node,
};

export default ChatComposer;
